import { useRef, useEffect, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import styled from "styled-components";

import LayoutSelector from "../components/LayoutSelector";
import { useFile } from "../Contexts";

// ── Constants ────────────────────────────────────────────────────────────────

const LAYOUT_TYPES = {
  random: "random",
  preset: "preset",
  grid: "grid",
  circle: "circle",
  concentric: "concentric",
  breadthfirst: "breadthfirst",
  cose: "cose",
};

const TOOLTIP_FEATURES = [
  "Balancing",
  "Cell Line",
  "Cell Type",
  "Compartment",
  "Compartment ID",
  "Const OFF",
  "Const ON",
  "Delay",
  "Element Database",
  "Element HGNC Symbol",
  "Element IDs",
  "Element Name",
  "Element Subtype",
  "Element Type",
  "Increment",
  "Levels",
  "Negative Connection Type List",
  "Negative List",
  "Negative Mechanism List",
  "Negative Regulation Rule",
  "Negative Regulator List",
  "Negative Site List",
  "Organism",
  "Paper IDs List",
  "Positive Connection Type List",
  "Positive List",
  "Positive Mechanism List",
  "Positive Regulation Rule",
  "Positive Regulator List",
  "Positive Site List",
  "Regulators",
  "Score List",
  "Source List",
  "Spontaneous",
  "Tissue Type",
  "Update Group",
  "Update Rank",
  "Update Rate",
  "Value Type",
];

const STYLESHEET = [
  {
    selector: "node",
    style: {
      width: 20,
      height: 20,
      "background-color": "orange",
      label: "data(label)",
      color: "#1f2937",
      "font-size": 8,
      "text-valign": "center",
      "text-halign": "center",
      "border-width": 1,
      "border-color": "#2c3d50",
    },
  },
  {
    selector: "node:hover",
    style: {
      "background-color": "#fff",
      "border-width": 2,
      "border-color": "#000",
      width: 25,
      height: 25,
    },
  },
  {
    selector: "edge",
    style: {
      width: 1,
      "line-color": "#000",
      "target-arrow-color": "#000",
      "target-arrow-shape": "triangle",
      "curve-style": "bezier",
      // label: "data(label)",
      "font-size": 3,
      "text-background-opacity": 1,
      "text-background-color": "#ffffff",
      "text-background-padding": 2,
      "text-rotation": "autorotate",
    },
  },
  {
    selector: "edge[label = 'decreases']",
    style: {
      "line-color": "#000",
      "target-arrow-color": "#e74c3c",
      "target-arrow-shape": "tee",
      "line-style": "solid",
    },
  },
  {
    selector: "edge[label = 'increases']",
    style: {
      "line-color": "#000",
      "target-arrow-color": "#27ae60",
      "target-arrow-shape": "triangle",
    },
  },
];

const TOOLTIP_CSS = `
  position: fixed;
  background: #1e293b;
  color: white;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.5;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  pointer-events: none;
  z-index: 99999;
  border: 1px solid #334155;
  display: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  max-width: 300px;
  // max-height: 400px;
  // overflow-y: auto;
`;

const ARROW_CSS = `
  position: absolute;
  top: 100%; left: 50%;
  transform: translateX(-50%);
  width: 0; height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #1e293b;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildTooltipHTML(label, features) {
  const rows = TOOLTIP_FEATURES.filter((key) => features[key])
    .map((key) => {
      const val = Array.isArray(features[key])
        ? features[key].join(", ")
        : features[key];
      return `
        <div style="display:flex;justify-content:space-between;gap:12px;margin:4px 0;">
          <span style="font-weight:500;color:#cbd5e0;white-space:nowrap;">${key}:</span>
          <span style="color:#e2e8f0;text-align:right;">${val}</span>
        </div>`;
    })
    .join("");
  return `<div style="font-weight:bold;margin-bottom:8px;color:#ffd700;">${label}</div>${rows}`;
}

// ── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e8f0;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  color: #1e40af;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

const GraphWrapper = styled.div`
  flex: 1;
  position: relative;
  background-image:
    radial-gradient(#d1d5db 1px, transparent 1px),
    radial-gradient(#d1d5db 1px, transparent 1px);
  background-position:
    0 0,
    6px 6px;
  background-size: 12px 12px;
  overflow: hidden;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 44px;
  margin-bottom: 14px;
  opacity: 0.4;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
`;

const EmptySubtitle = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;

// ── Component ────────────────────────────────────────────────────────────────

export default function Graph({ selectedFile }) {
  const cyRef = useRef(null);
  const tooltipRef = useRef(null);
  const [layout, setLayout] = useState("cose");

  const { interactionModel, files, setInteractionModel } = useFile();

  // Clear model when all files are removed
  useEffect(() => {
    if (!files?.length) setInteractionModel(null);
  }, [files, setInteractionModel]);

  // Create imperative tooltip element
  useEffect(() => {
    const tooltip = document.createElement("div");
    tooltip.style.cssText = TOOLTIP_CSS;
    document.body.appendChild(tooltip);
    tooltipRef.current = tooltip;
    return () => {
      document.body.removeChild(tooltip);
      tooltipRef.current = null;
    };
  }, []);

  // Stop all animations on unmount to prevent notify-on-null errors
  useEffect(() => {
    return () => {
      const cy = cyRef.current;
      if (cy && !cy.destroyed()) {
        cy.stop(true, true);
        cy.elements().stop(true, true);
      }
      cyRef.current = null;
    };
  }, []);

  // Re-run layout whenever it changes (animate:false avoids frame loop errors)
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || cy.destroyed() || !cy.elements().length) return;
    try {
      const l = cy.layout({ name: layout, animate: false });
      l.run();
    } catch (e) {
      // ignore
    }
  }, [layout]);

  // Attach Cytoscape event listeners
  useEffect(() => {
    const cy = cyRef.current;
    const tooltip = tooltipRef.current;
    if (!cy || !tooltip) return;

    cy.fit();

    const showTooltip = (event) => {
      const node = event.target;
      const features = node.data("features");
      if (!features) return;

      // Use mouse event for accurate viewport position
      const originalEvent = event.originalEvent;
      const mouseX = originalEvent.clientX;
      const mouseY = originalEvent.clientY;

      tooltip.innerHTML = buildTooltipHTML(node.data("label"), features);

      // Remove old arrow if any
      const oldArrow = tooltip.querySelector(".tooltip-arrow");
      if (oldArrow) oldArrow.remove();
      const arrow = document.createElement("div");
      arrow.className = "tooltip-arrow";
      arrow.style.cssText = ARROW_CSS;
      tooltip.appendChild(arrow);

      tooltip.style.display = "block";

      // Position above cursor, centered horizontally
      const tw = tooltip.offsetWidth;
      const th = tooltip.offsetHeight;
      let left = mouseX - tw / 2;
      let top = mouseY - th - 12;

      // Keep within viewport
      if (left < 8) left = 8;
      if (left + tw > window.innerWidth - 8) left = window.innerWidth - tw - 8;
      if (top < 8) top = mouseY + 20; // flip below if too close to top

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      tooltip.style.transform = "none";
    };

    const hideTooltip = () => {
      tooltip.style.display = "none";
    };

    cy.on("mouseover", "node", showTooltip);
    cy.on("mouseout", "node", hideTooltip);
    cy.on("pan", hideTooltip);
    cy.on("zoom", hideTooltip);

    return () => {
      if (!cy.destroyed()) {
        cy.removeListener("mouseover", "node", showTooltip);
        cy.removeListener("mouseout", "node", hideTooltip);
        cy.removeListener("pan", hideTooltip);
        cy.removeListener("zoom", hideTooltip);
      }
    };
  }, []);

  const elements = interactionModel
    ? [...interactionModel.nodes, ...interactionModel.edges]
    : [];
  return (
    <Container>
      <Toolbar>
        {selectedFile && (
          <FileInfo title={selectedFile.name}>📊 {selectedFile.name}</FileInfo>
        )}
        <LayoutSelector
          handleSelectedLayout={setLayout}
          layouts={LAYOUT_TYPES}
          selected={layout}
        />
      </Toolbar>

      {elements.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🔬</EmptyIcon>
          <EmptyTitle>No model loaded</EmptyTitle>
          <EmptySubtitle>
            Select a file from Uploaded Files to visualize the network
          </EmptySubtitle>
        </EmptyState>
      ) : (
        <GraphWrapper>
          <CytoscapeComponent
            elements={elements}
            stylesheet={STYLESHEET}
            style={{ width: "100%", height: "100%" }}
            layout={{ name: "preset" }}
            cy={(cy) => {
              cyRef.current = cy;
              cy.userPanningEnabled(true);
              cy.userZoomingEnabled(true);
              cy.boxSelectionEnabled(true);
              cy.fit();
              cy.on("destroy", () => {
                cyRef.current = null;
              });
            }}
          />
        </GraphWrapper>
      )}
    </Container>
  );
}
